import Axios, { AxiosResponse } from 'axios';
import React, { useEffect, useState } from 'react';
import { useDispatch, useStore, useSelector } from 'react-redux';
import { FormController, useOneFormContext } from 'src/feature-app';
import { FormDataGroup, FormInfo, OneFormState, Steps, MolecularResponse } from 'src/types';
import { OneElementTemplateFullScreen } from 'src/feature-app/Screen';
import { Container, ContainerPadding, Text, TextAppearance } from '@volkswagen-onehub/components-core';
import { MultipleCTASelection, MultipleOptionCTAs, DetalleMotivo, SuggestModelo } from 'src/components';
import { ConfirmacionNoRenovacion, ThankYouNoRenovacion, FakeTriggerCTA } from '.';
import { formatHeaders, formDataBuilder } from 'src/forms/format-fields';
import { dpsSend } from 'src/bff';
import { getParamsForm } from 'src/feature-app/get-params-form';
import { NoRenovacionFormProps } from './NoRenovacionController';
import { useInitializeNoRenovacionForm } from './use-initialize-no-renovacion-form';
import { useTrackingManager } from 'src/feature-app/hooks/use-tracking-manager';
import { addRemovedStep, getIndexInSteps, removeStep, resetStepsScreenIndex, removeMultipleSteps } from '../helpers/add-remove-steps';
import { CTAsMotivoRenovadorYaTengo, CTAsUsadoNuevo, CTAsLugarCompra } from 'src/feature-app/InputsValues';

export enum NoRenovacionNewCar {
  Introduccion,
  Modelo,
  UsadoNuevo,
  LugarCompra,
  Motivo,
  MasInformacion,
  Confirmacion,
}

export function NoRenovacionYaTengo({
  dpsData,
  idVehicle,
  idPerson,
  handleError,
  trackFormLoad,
  estado,
}: NoRenovacionFormProps) {
  const { initializeForm, handleNextStep } = useOneFormContext();
  const trackingManager = useTrackingManager();
  const store = useStore();
  const dispatch = useDispatch();
  const { formData } = useSelector((state: OneFormState) => state);
  const [loadingPresubmit, setLoadingPresubmit] = useState(false);
  const { multipleChoice, brand } = formData.fields;

  const handleMotivo = (brand: string) => {
    const { steps: actualSteps, screenIndex } = store.getState().formInfo;
    let newSteps = [...actualSteps];

    if (brand !== 'Volkswagen') {
      const multipleChoide = getIndexInSteps(newSteps, 'multipleChoice');
      const masInformacion = getIndexInSteps(newSteps, 'masInformacion');

      if (multipleChoide === -1) {
        newSteps = addRemovedStep(steps, actualSteps, 'masInformacion', screenIndex);
        setTimeout(() => {
          handleNextStep();
        }, 0);
      }
      if (masInformacion === -1) {
        newSteps = addRemovedStep(steps, actualSteps, 'masInformacion', screenIndex);
        setTimeout(() => {
          handleNextStep();
        }, 0);
      }

      newSteps = removeStep(newSteps, 'lugarCompra');
    } else {
      const lugarCompra = getIndexInSteps(newSteps, 'lugarCompra');

      if (lugarCompra === -1) {
        newSteps = addRemovedStep(steps, actualSteps, 'lugarCompra', screenIndex);
        setTimeout(() => {
          handleNextStep();
        }, 0);
      }
      newSteps = removeMultipleSteps(newSteps, ['multipleChoice', 'masInformacion']);
    }

    const newStepsWithIndex = resetStepsScreenIndex(newSteps);

    dispatch({ type: 'UPDATE_STEPS', payload: newStepsWithIndex });
    dispatch({ type: 'UPDATE_LAST_STEP', payload: newStepsWithIndex[newStepsWithIndex.length - 1] });
    dispatch({ type: 'SET_NUMBER_OF_SCREENS', payload: newStepsWithIndex.length });
  };

  const handleDetalleMotivo = (multipleChoice: Array<string>) => {
    const { steps: actualSteps, screenIndex } = store.getState().formInfo;
    let newSteps = [...actualSteps];

    if (multipleChoice.includes('otros motivos')) {
      const stepIndex = getIndexInSteps(newSteps, 'masInformacion');

      if (stepIndex === -1) {
        newSteps = addRemovedStep(steps, actualSteps, 'masInformacion', screenIndex);
        setTimeout(() => {
          handleNextStep();
        }, 0);
      }
    } else {
      newSteps = removeStep(newSteps, 'masInformacion');
    }

    const newStepsWithIndex = resetStepsScreenIndex(newSteps);

    dispatch({ type: 'UPDATE_STEPS', payload: newStepsWithIndex });
    dispatch({ type: 'UPDATE_LAST_STEP', payload: newStepsWithIndex[newStepsWithIndex.length - 1] });
    dispatch({ type: 'SET_NUMBER_OF_SCREENS', payload: newStepsWithIndex.length });
  };

  useEffect(() => {
    const sendPresubmit = async () => {
      const result = await sendForm(true);

      if (!result) {
        handleError();
      }
      setLoadingPresubmit(false);
    };

    sendPresubmit();
  }, []);

  const steps: Steps[] = [
    {
      title: (
        <>
          <Text bold>Nos encanta viajar contigo, pero sabemos que la vida da muchas vueltas y tenemos curiosidad…{' '}</Text>
          ¿Qué modelo te ha conquistado?
        </>
      ),
      fields: <OneElementTemplateFullScreen allowOverflowingContent element={<FakeTriggerCTA />} />,
      screenIndex: NoRenovacionNewCar.Introduccion,
      name: 'introduccion',
    },
    {
      title: (
        <>
          <Text bold>
            ¿Qué modelo{' '}
            <span style={{ whiteSpace: 'nowrap' }}>
              elegiste?<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
            </span>
          </Text>
        </>
      ),
      fields: (
        <OneElementTemplateFullScreen
          allowOverflowingContent
          element={
            <div style={{ marginBottom: '2px', marginTop: '44px' }}>
              <SuggestModelo
                notALayer
                marginTop="0"
                fieldsName="modelo"
                required
                outputsSeparados
                nextOnClick
              />
            </div>
          }
        />
      ),
      screenIndex: NoRenovacionNewCar.Modelo,
      name: 'Modelo',
      outputs: ['brand', 'model'],
      outputsText: ['brand', 'model'],
      showOverflow: true,
    },
    {
      title: (
        <Text bold>
          ¿Qué opción has{' '}
          <span style={{ whiteSpace: 'nowrap' }}>
            escogido?<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
          </span>
        </Text>
      ),
      fields: (
        <OneElementTemplateFullScreen
          element={<MultipleCTASelection ctaValues={CTAsUsadoNuevo} name="usadoNuevo" nextOnClick />}
        />
      ),
      screenIndex: NoRenovacionNewCar.UsadoNuevo,
      name: 'usadoNuevo',
      outputs: ['usadoNuevo'],
      outputsText: ['usadoNuevo'],
    },
    {
      title: (
        <Text bold>
          ¿Dónde has adquirido tu nuevo{' '}
          <span style={{ whiteSpace: 'nowrap' }}>
            vehículo?<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
          </span>
        </Text>
      ),
      fields: (
        <OneElementTemplateFullScreen
          element={<MultipleCTASelection ctaValues={CTAsLugarCompra} name="lugarCompra" nextOnClick />}
        />
      ),
      screenIndex: NoRenovacionNewCar.LugarCompra,
      name: 'lugarCompra',
      outputs: ['lugarCompra'],
      outputsText: ['lugarCompra'],
    },
    {
      title: (
        <>
          <Text bold>¿Por qué motivos </Text>
          no has elegido un{' '}
          <span style={{ whiteSpace: 'nowrap' }}>
            Volkswagen?<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
          </span>
          <Container padding={{ top: ContainerPadding.dynamic0020 }}>
            <Text appearance={TextAppearance.copy0200} staticSize>
              Elige un máximo de 3 opciones
            </Text>
          </Container>
        </>
      ),
      alternateTitle: (
        <>
          <Text bold>¿Por qué motivos </Text>
          no has elegido un{' '}
          <span style={{ whiteSpace: 'nowrap' }}>
            Volkswagen?<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
          </span>
        </>
      ),
      fields: (
        <OneElementTemplateFullScreen
          element={
            <MultipleOptionCTAs
              ignoreHandleClick={true}
              ctaValues={CTAsMotivoRenovadorYaTengo}
              minSelected={1}
              maxSelected={3}
              name="multipleChoice"
            />
          }
        />
      ),
      screenIndex: NoRenovacionNewCar.Motivo,
      name: 'multipleChoice',
      outputs: ['multipleChoice'],
      outputsText: ['multipleChoice'],
    },
    {
      title: (
        <>
          <Text bold>
            ¿Nos puedes decir cuáles{' '}
            <span style={{ whiteSpace: 'nowrap' }}>
              son?<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
            </span>
          </Text>
        </>
      ),
      fields: <OneElementTemplateFullScreen element={<DetalleMotivo />} />,
      screenIndex: NoRenovacionNewCar.MasInformacion,
      name: 'masInformacion',
      outputs: ['masInformacion'],
      outputsText: ['masInformacion'],
    },
    {
      fields: <ConfirmacionNoRenovacion idPerson={idPerson} />,
      screenIndex: NoRenovacionNewCar.Confirmacion,
      name: 'Confirmacion',
      outputs: ['confirmacion'],
      outputsText: ['confirmacion'],
    },
  ];

  const NuevoFormData: Partial<FormDataGroup> = {
    fields: { formName: 'no_renovacion-ya_tengo', formType: 'otherform', pageCategory: 'Encuesta' }, // Analítica
  };

  const getFormInfo = (): {
    firstStep: Steps;
    lastStep: Steps;
    stepsHistory: number[];
    numberOfScreens: number;
    newSteps?: Steps[];
  } => {
    return {
      firstStep: steps[0],
      lastStep: steps[steps.length - 1],
      stepsHistory: [0],
      numberOfScreens: steps.length,
      newSteps: null,
    };
  };

  const generateData = (data?: Partial<FormDataGroup>) => {
    const { formData, dealersInfo }: OneFormState = store.getState();
    const { fields, headers } = formData;
    const { tc, today, url, device } = getParamsForm();

    return {
      'headers[TC]': tc,
      'headers[PINCODE]': dpsData.pincode.pincodeResponse.params.pincode,
      'headers[FECHA_INSERTION_DATE]': today,
      'headers[DESC_BRAND]': 'VW',
      'headers[DESC_GDPR_ADAPTED]': 'true',
      'headers[DESC_ID_CAMPAIGN]': '7011n000000JvhCAAS',
      'headers[DESC_CAMPAIGNBLOCK]': 'B19 - Renovadores',
      'headers[DETALLE_TIPO_PETICION_SIM]': 'Encuesta Renovación',
      'headers[DESC_FORMOBJECTIVE]': 'Encuesta',

      'headers[DESC_TOKEN]': 'VW_DDB_FORM_NORENUEVA', // CAMBIA PRESUMBIT/SUBMIT
      'headers[DESC_FORM_DETAIL]': 'NoRenovación Grabación', // CAMBIA PRESUMBIT/SUBMIT
      'headers[DESC_DEVICETYPE]': device,
      'headers[COD_FORM_ID]': fields.formName ? `vw-oneform-${fields.formName}` : `vw-oneform-${data.fields.formName}`,
      'headers[DESC_URLORIGIN]': url,
      'headers[DESC_ID_CLIMAPRO]': idPerson.ID_Person_DIM_IMP__c,
      'headers[DESC_ID_VEHICLE_CLIMAPRO]': idVehicle.IDClimapro__c,

      'fields[DESC_NAME]': idPerson.FirstName,
      'fields[DESC_SURNAME_1]': idPerson.LastName,
      'fields[DESC_SURNAME_2]': idPerson.TXT_2_Surname__c,
      'fields[NUM_CELLPHONE]': idPerson.NUM_Phone_mobile__c,
      'fields[DESC_EMAIL_ADDRESS]': idPerson.TXT_Email__c,

      'fields[DESC_LOPD]': 'PERMITE', // SOLO EN SUBMIT
      'fields[DESC_TRANF_DATA_THIRD]': 'NO INFORMADO', // SOLO EN SUBMIT
      'fields[DESC_LSSI]': fields.lssi ? fields.lssi : 'NO INFORMADO',
      'fields[DESC_LOPD_GDPR_PERFILADO]': fields.tmk ? fields.tmk : 'NO INFORMADO',
      'fields[DESC_FORM_VERSION]': fields.formName
        ? `vw-oneform-${fields.formName}-${process.env.VERSION}`
        : `vw-oneform-${data.fields.formName}-${process.env.VERSION}`,
      'fields[DESC_AUXFIELD1]': 'Ya tengo',

      'fields[DESC_ACTUAL_VEHICLE_BRAND]': fields.brand,
      'fields[DESC_ACTUAL_VEHICLE_MODEL]': fields.model,
      'fields[DESC_AUXFIELD2]': fields.usadoNuevo,
      'fields[DESC_AUXFIELD3]': fields.multipleChoice,
      'fields[DESC_AUXFIELD4]': fields.masInformacion,
      'fields[DESC_AUXFIELD16]': fields.lugarCompra,
      'fields[DESC_DATA_ENRICHMENT]': 'NO INFORMADO',

      'flags[FLAG_ISLEAD]': 'false',
      'flags[FLAG_ISPROSPECT]': 'false',
      'flags[FLAG_GENERATELEM]': 'false',
    };
  };

  const generateSubmitData = () => generateData();

  /**
   * Generamos la misma data que para el submit y eliminamos todos los campos que no son necesarios para el presubmit.
   */
  const generatePresubmitData = () => {
    const data = generateData(NuevoFormData);

    //
    delete data['fields[DESC_TRANF_DATA_THIRD]'];
    //
    delete data['headers[DETALLE_TIPO_PETICION_SIM]'];
    delete data['headers[PINCODE]'];
    delete data['flags[FLAG_ISLEAD]'];
    delete data['flags[FLAG_ISPROSPECT]'];
    delete data['flags[FLAG_GENERATELEM]'];
    delete data['fields[DESC_AUXFIELD2]'];
    delete data['fields[DESC_AUXFIELD3]'];
    delete data['fields[DESC_AUXFIELD4]'];
    delete data['fields[DESC_AUXFIELD16]'];

    delete data['fields[DESC_ACTUAL_VEHICLE_BRAND]'];
    delete data['fields[DESC_ACTUAL_VEHICLE_MODEL]'];

    delete data['fields[DESC_LOPD]'];
    delete data['fields[DESC_LSSI]'];
    delete data['fields[DESC_LOPD_GDPR_PERFILADO]'];
    delete data['fields[DESC_DATA_ENRICHMENT]'];

    data['fields[DESC_AUXFIELD1]'] = 'Ya tengo';
    data['headers[DESC_FORM_DETAIL]'] = 'NoRenovación Pregrabación';
    data['headers[DESC_TOKEN]'] = 'VW_DDB_FORM_NORENUEVA_PRE';

    return data;
  };

  const sendForm = async (isPresubmit?: boolean): Promise<boolean> => {
    return new Promise<any>(async (resolve, reject) => {
      const data = isPresubmit ? generatePresubmitData() : generateSubmitData();
      const dataStringified = JSON.stringify(data);
      const dataParsed = JSON.parse(dataStringified);

      const formData = formDataBuilder(dataParsed);

      const response = await dpsSend(formData);

      if (response.data?.content?.code === '200') {
        dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
        resolve(true);
      } else {
        resolve(false);
      }
    });
  };

  const { firstStep, lastStep, loading } = useInitializeNoRenovacionForm(steps);

  useEffect(() => {
    const NuevoFormInfo: Partial<FormInfo> = {
      formStarted: true,
      sendFormCallback: sendForm,
      fullScreen: firstStep.fullScreen ? true : false,
      lastStep,
      numberOfScreens: steps.length,
      screenIndex: firstStep.screenIndex,
      formTheme: 'main',
      notALayer: true,
      formType: 'inPage',
      step: firstStep,
      steps,
      stepsHistory: [0],
      thankYouComponent: <ThankYouNoRenovacion />,
    };

    if (!loading) {
      initializeForm(NuevoFormInfo, NuevoFormData, true);
      trackFormLoad();
    }
  }, [loading]);

  useEffect(() => {
    if (brand) handleMotivo(brand);
  }, [brand]);

  useEffect(() => {
    if (multipleChoice) handleDetalleMotivo(multipleChoice);
  }, [multipleChoice]);


  return loading || loadingPresubmit || !steps ? null : <FormController steps={steps} screenType="full-screen" />;
}
